import { useState, useEffect } from 'react'
import { authorization } from '../../../Module/Auth/Auth'
import { CONFIG } from '../../../Config/config'

export const DESIGN_STATUS = {
  ACTIVE: 0,
  ARCHIVED: -1,
}
/**
 * Call Graph QL Endpoint
 * @param {string} query
 * @param {object} variables
 * @returns {Promise<object>}
 */
export const callAuthorizedGraphQLEndpoint = function(
  operationName,
  query,
  variables,
) {
  const authToken = authorization.getAPIKey()
  const body = JSON.stringify({
    operationName,
    query,
    variables,
  })

  return fetch(`${CONFIG.FLYER_API_URL}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'authorization': `Bearer ${authToken}`,
    },
    body,
  })
}

const getDesignsGQL = async (
  clientId,
  filters = {},
  pagination = { page: 1, limit: 20 },
) => {
  const designs = await callAuthorizedGraphQLEndpoint(
    'getDesigns',
    `query getDesigns($clientId: ID, $filters: DesignFilters, $pagination: PaginationInput){
    getDesigns(clientId: $clientId, filters: $filters, pagination: $pagination){
      designs {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        designSource
        clientId
        thumbnailUrl
        updatedAt
        createdAt
        format
        status
        authorId
        author {
          id
          fullName
        }
      }
      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
      }
    }
    }`,
    {
      clientId,
      filters,
      pagination,
    },
  )
  const data = await designs.json()

  const designData = data.data.getDesigns
  const processedDesigns = designData.designs.map(design => ({
    id: design.id,
    imageSrc: design.thumbnailUrl,
    designName: design.filename,
    format: design.format,
    designer: (design.author && design.author.fullName) || '-',
    designSource: design.designSource,
    dateCreated: design.createdAt ? new Date(Number(design.createdAt)) : null,
    status: design.status,
    updatedAt: design.updatedAt,
    s3Link: design.s3Link,
    s3CmykLink: design.s3CmykLink,
    clientId: design.clientId,
  }))

  return {
    designs: processedDesigns,
    pagination: designData.pagination,
  }
}

// Template-related GraphQL functions
const getDesignTemplatesGQL = async () => {
  const templates = await callAuthorizedGraphQLEndpoint(
    'getDesignTemplates',
    `query getDesignTemplates{
      getDesignTemplates{
        id
        name
        description
        roomId
        thumbnail
        isPublic
        orientation
        format
        promoOffer
        industry
        useCase
        uspsCompliant
        createdAt
        updatedAt
      }
    }`,
    {},
  )
  const data = await templates.json()

  return (
    data.data.getDesignTemplates.map(template => ({
      id: template.id,
      imageSrc: template.thumbnail,
      designName: template.name,
      format: template.format,
      designer: `Promofy`,
      dateCreated: new Date(Number(template.createdAt)),
      status: template.isPublic ? 'public' : 'private',
    })) || []
  )
}

const createDesignGQL = async (clientId, fileName = '', authorId = null) => {
  const design = await callAuthorizedGraphQLEndpoint(
    'createDesign',
    `mutation createDesign($clientId: ID!, $design: DesignInput!) {
      createDesign(clientId: $clientId, design: $design) {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        createdAt
        format
        status
        authorId
        author {
          id
          fullName
        }
      }
    }`,
    {
      clientId,
      design: {
        filename: fileName,
        s3Link: '',
        s3CmykLink: '',
        isUploaded: false,
        format: 'TEARAWAY',
        status: DESIGN_STATUS.ACTIVE,
        authorId,
      },
    },
  )
  const data = await design.json()
  return {
    id: data.data.createDesign.id,
    imageSrc: data.data.createDesign.thumbnailUrl,
    designName: data.data.createDesign.filename,
    format: data.data.createDesign.format,
    designer:
      (data.data.createDesign.author &&
        data.data.createDesign.author.fullName) ||
      '-',
    dateCreated: new Date(Number(data.data.createDesign.createdAt)),
    status: data.data.createDesign.status,
  }
}

const updateDesignGQL = async payload => {
  const res = await callAuthorizedGraphQLEndpoint(
    'updateDesign',
    `mutation updateDesign($designId: ID!, $design: DesignInput!) {
      updateDesign(designId: $designId, design: $design) {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        createdAt
        thumbnailUrl
        updatedAt
        format
        status
        authorId
        author {
          id
          fullName
        }
      } 
    }`,
    {
      designId: payload.designId,
      design: payload.design,
    },
  )

  const data = await res.json()

  return {
    id: data.data.updateDesign.id,
    imageSrc: data.data.updateDesign.thumbnailUrl,
    designName: data.data.updateDesign.filename,
    format: data.data.updateDesign.format,
    dateCreated: new Date(Number(data.data.updateDesign.createdAt)),
    status: data.data.updateDesign.status,
  }
}

const duplicateDesignGQL = async (clientId, originalDesignId) => {
  const original = await getDesignGQL(originalDesignId)
  // Create a new design with the same content but different name
  const newDesign = await callAuthorizedGraphQLEndpoint(
    'createDesign',
    `mutation createDesign($clientId: ID!, $design: DesignInput!) {
      createDesign(clientId: $clientId, design: $design) {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        createdAt
        thumbnailUrl
        htmlContent
        format
        status
        authorId
        updatedAt
        author {
          id
          fullName
        }
      }
    }`,
    {
      clientId,
      design: {
        filename: `${original.filename} (Copy)`,
        s3Link: original.s3Link,
        s3CmykLink: original.s3CmykLink,
        isUploaded: original.isUploaded,
        format: original.format,
        status: DESIGN_STATUS.ACTIVE,
        authorId: original.authorId,
        thumbnailUrl: original.thumbnailUrl,
        htmlContent: original.htmlContent,
      },
    },
  )

  const data = await newDesign.json()
  return {
    id: data.data.createDesign.id,
    imageSrc: data.data.createDesign.thumbnailUrl,
    designName: data.data.createDesign.filename,
    format: data.data.createDesign.format,
    designer:
      (data.data.createDesign.author &&
        data.data.createDesign.author.fullName) ||
      '-',
    dateCreated: new Date(Number(data.data.createDesign.createdAt)),
    status: data.data.createDesign.status,
    htmlContent: data.data.createDesign.htmlContent,
  }
}
const getDesignGQL = async designId => {
  const design = await callAuthorizedGraphQLEndpoint(
    'getDesign',
    `query getDesign($id: ID!) {
      getDesign(id: $id) {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        createdAt
        thumbnailUrl
        updatedAt
        format
        status
        authorId
        htmlContent
        author {
          id
          fullName
        }
      }
    }`,
    {
      id: designId,
    },
  )
  const data = await design.json()
  return data.data.getDesign
}

const useDesignManagement = ({
  clientId,
  filters,
  pagination,
  onPaginationChange,
}) => {
  const [designs, setDesigns] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [paginationInfo, setPaginationInfo] = useState(null)

  const fetchDesigns = async (
    currentFilters = filters,
    currentPagination = pagination,
  ) => {
    setLoading(true)
    try {
      const [designsResult, templatesResult] = await Promise.all([
        getDesignsGQL(clientId, currentFilters, currentPagination),
        getDesignTemplatesGQL(),
      ])

      setDesigns(designsResult.designs)
      setPaginationInfo(designsResult.pagination)
      setTemplates(templatesResult)
      setLoading(false)

      if (onPaginationChange) {
        onPaginationChange(designsResult.pagination)
      }
    } catch (error) {
      console.error('Error fetching designs:', error)
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDesigns()
  }, [clientId, JSON.stringify(filters), JSON.stringify(pagination)])

  const createDesign = async (fileName, authorId) => {
    setLoading(true)
    const res = await createDesignGQL(clientId, fileName, authorId)
    setDesigns(prev => [...prev, res])
    setLoading(false)
    return res
  }

  const updateDesign = async payload => {
    setLoading(true)
    const res = await updateDesignGQL(payload)
    setDesigns(prev =>
      prev.map(d => (d.id === payload.designId ? { ...d, ...res } : d)),
    )
    setLoading(false)
    return res
  }

  const duplicateDesign = async (designId, options = {}) => {
    const { showLoading = false } = options
    if (showLoading) {
      setLoading(true)
    }
    try {
      const res = await duplicateDesignGQL(clientId, designId)
      setDesigns(prev => [...prev, res])
      if (showLoading) {
        setLoading(false)
      }
      return res
    } catch (error) {
      if (showLoading) {
        setLoading(false)
      }
      throw error
    }
  }

  const getDesign = async designId => {
    setLoading(true)
    const res = await getDesignGQL(designId)
    setLoading(false)
    return res
  }

  const refreshDesign = async designId => {
    // Fetch the latest design data including updated thumbnail
    const updatedDesign = await getDesignGQL(designId)

    // Update the design in the local state with refreshed data
    setDesigns(prev =>
      prev.map(design => {
        if (design.id === designId) {
          return {
            ...design,
            imageSrc: updatedDesign.thumbnailUrl,
            designName: updatedDesign.filename,
            format: updatedDesign.format,
            dateCreated: updatedDesign.createdAt
              ? new Date(Number(updatedDesign.createdAt))
              : null,
            status: updatedDesign.status,
            updatedAt: updatedDesign.updatedAt,
            s3Link: updatedDesign.s3Link,
            s3CmykLink: updatedDesign.s3CmykLink,
          }
        }
        return design
      }),
    )

    return updatedDesign
  }

  return {
    designs,
    templates,
    setDesigns,
    createDesign,
    updateDesign,
    duplicateDesign,
    getDesign,
    refreshDesign,
    loading,
    paginationInfo,
    fetchDesigns,
  }
}

export default useDesignManagement
